import ReactECharts from 'echarts-for-react';
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Paper, Skeleton } from '@mui/material';
import { toast } from '../../../../ui/toast';
import { Modal } from '../../../../ui/modal/modal';
import {
  Applicant,
  SegmentType,
} from '../../../../utils/types/JobOpenings';
import { ScatterModal } from '../ModalContent/ScatterModal';
import { SegmentModal } from '../ModalContent/segmentModal';
import { Bar3D } from '../ui/Charts/Bar3D';
import { AnalyticAction } from './AnalysisSideMenu/AnalyticAction';
import { AnalyticThreshold } from './AnalysisSideMenu/AnalyticThreshold';
import { DEFAULT_SEGMENTS } from './Constants/DefaultSefmantTypes';
import { Get2DChartOptions } from './Constants/Get2DChartOptions';
import { ITwoDChartProps } from './Interfaces/I2DChartProps';
import styles from './Modal.module.css';

export const TwoDChart: React.FC<ITwoDChartProps> = (props) => {
  const {
    tabTitles,
    setTabTitles,
    setActiveTab,
    selectedCandidates,
    setSelectedCandidates,
    view3D,
    searchCSVObject,
    setSearchCSVObject,
    candidates,
    skillValue,
    experienceValue,
    setSkillValue,
    setExperienceValue,
    loading,
    scatter,
    svgActiveTab,
    useSkillPercentage,
    markLines,
    nineBox,
    colorList,
    projection,
    boxWidthDepth,
    maxLegion,
    isSeriesModal,
    setIsSeriesModal,
    seriesCandidates,
    setSeriesCandidates,
    additionalSkills,
    jobTitles,
    keywords,
    behavioralAttributes,
    onCandidateClick,
    allCandidates,
    setIsProfileModalOpen,
    setProfileModalCandidates,
    setProfileModalInitialIndex,
    setProfileModalTileName,
    showHeadshots = false,
  } = props;

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  // const [isSeriesModal, setIsSeriesModal] = useState<boolean>(false);
  const [segmentTitle, setSegmentTitle] = useState<string>();
  const [currentTileName, setCurrentTileName] = useState<string>('');
  const [segmentedCandidates, setSegmentedCandidates] = useState<Applicant[]>(
    [],
  );
  const [xCoords, setXCoords] = useState<number[]>([]);
  const [yCoords, setYCoords] = useState<number[]>([]);
  const [actionSelected, setActionSelected] = useState<string[]>([]);
  const [isCompactChart, setIsCompactChart] = useState(false);
  // const [seriesCandidates, setSeriesCandidates] = useState<Applicant[]>([]);

  const loadSeriesModalContent = () => {
    const handleTileBasedCandidateClick = (candidate: Applicant) => {
      if (onCandidateClick) {
        // Pass the tile candidates and tile name to the profile modal
        onCandidateClick(candidate, seriesCandidates, currentTileName);
        setIsSeriesModal(false);
      }
    };

    return (
      <ScatterModal
          segmentedCandidates={seriesCandidates}
          selectedCandidates={selectedCandidates}
          setSelectedCandidates={setSelectedCandidates}
          setActiveTab={setActiveTab}
          setTabTitles={setTabTitles}
          tabTitles={tabTitles}
          useSkillPercentage={useSkillPercentage}
          onCandidateClick={handleTileBasedCandidateClick}
          currentTab={props.currentTab}
          isApplicantSelected={props.isApplicantSelected}
      />
    );
  };

  const [segmentedData, setSegmentedData] = useState<SegmentType[][]>(
    DEFAULT_SEGMENTS({ skillValue, experienceValue }),
  );

  useEffect(() => {
    setSearchCSVObject({
      ...searchCSVObject,
      skills: {
        ...searchCSVObject.skills,
        min: skillValue[0],
        max: skillValue[1],
      },
      experience: {
        ...searchCSVObject.experience,
        min: experienceValue[0],
        max: experienceValue[1],
      },
    });
    setSegmentedData(DEFAULT_SEGMENTS({ skillValue, experienceValue }));
  }, [experienceValue, skillValue]);

  useEffect(() => {
    const updateCompactState = () => {
      setIsCompactChart(window.innerWidth < 768);
    };

    updateCompactState();
    window.addEventListener('resize', updateCompactState);
    return () => window.removeEventListener('resize', updateCompactState);
  }, []);

  const chartHeight = useMemo(() => {
    if (isCompactChart) {
      // navbar ~56px + toolbar ~56px + legend ~40px + padding/gap ~24px = 176px
      const available = Math.max(300, window.innerHeight - 176);
      return `${available}px`;
    }
    if (svgActiveTab !== -1) return '620px';
    return '640px';
  }, [isCompactChart, svgActiveTab]);

  const loadModalContent = () => {
    const handleTileBasedCandidateClick = (candidate: Applicant) => {
      if (onCandidateClick) {
        // Pass the tile candidates and tile name to the profile modal
        onCandidateClick(candidate, segmentedCandidates, segmentTitle || '');
        setIsModalOpen(false);
      }
    };

    return (
      <SegmentModal
          segmentedCandidates={segmentedCandidates}
          xCoords={xCoords}
          yCoords={yCoords}
          setIsModalOpen={setIsModalOpen}
          selectedCandidates={selectedCandidates}
          setSelectedCandidates={setSelectedCandidates}
          setActiveTab={setActiveTab}
          setTabTitles={setTabTitles}
          tabTitles={tabTitles}
          useSkillPercentage={useSkillPercentage}
          onCandidateClick={handleTileBasedCandidateClick}
          currentTab={props.currentTab}
          isApplicantSelected={props.isApplicantSelected}
      />
    );
  };

  const renderTabComponent = () => {
    if (svgActiveTab === 0) {
      return <></>;
    } else if (svgActiveTab === 1) {
      return (
        <AnalyticThreshold
            setExperienceValue={setExperienceValue}
            experienceValue={experienceValue}
            expTitle='Total Years of Experience'
            skillTitle='Total number of Skills'
            skillValue={skillValue}
            setSkillValue={setSkillValue}
        />
      );
    } else if (svgActiveTab === 2) {
      return <></>;
    } else if (svgActiveTab === 3) {
      return (
        <AnalyticAction
            allCandidates={allCandidates}
            setIsProfileModalOpen={setIsProfileModalOpen}
            setProfileModalCandidates={setProfileModalCandidates}
            setProfileModalInitialIndex={setProfileModalInitialIndex}
            setProfileModalTileName={setProfileModalTileName}
            handleCandidateClickFromChart={onCandidateClick}
            actionSelected={actionSelected}
            setActionSelected={setActionSelected}
            setSegmentedData={setSegmentedData}
            defaultSegment={DEFAULT_SEGMENTS({ skillValue, experienceValue })}
            loading={loading}
        />
      );
    }
  };

  const onChartClick = (param: any) => {
    if (param.data && param.data.name) {
      if (param.componentType === 'markArea') {
        if (svgActiveTab === 3) {
          // Actions tab: Handle selection logic
          let isSelected = false;
          setSegmentedData((prevData) => {
            return prevData.map((segment) => {
              if (segment[0].id === param.data.id) {
                isSelected = segment[0].itemStyle?.color === 'green';
                return segment.map((point) => ({
                  ...point,
                  itemStyle: {
                    color: isSelected ? undefined : 'green',
                  },
                }));
              }
              return segment;
            });
          });

          // Get candidates in the clicked segment/box
          const filteredData = candidates.filter((item) => {
            return (
              item.value[0] >= param.data.coord[0][0] &&
              item.value[0] <= param.data.coord[1][0] &&
              item.value[1] >= param.data.coord[0][1] &&
              item.value[1] <= param.data.coord[1][1]
            );
          });

          const userIds = filteredData.map((user) => user.userId);

          if (!isSelected) {
            // Select all candidates in this box
            setActionSelected((prev) => {
              const mergedIds = Array.from(new Set([...prev, ...userIds]));
              return mergedIds;
            });
          } else {
            // Deselect all candidates in this box
            setActionSelected((prev) => {
              return prev.filter((id) => !userIds.includes(id));
            });
          }
        } else {
          // Other tabs: Open segment modal
          const filteredData = candidates.filter((item) => {
            return (
              item.value[0] >= param.data.coord[0][0] &&
              item.value[0] <= param.data.coord[1][0] &&
              item.value[1] >= param.data.coord[0][1] &&
              item.value[1] <= param.data.coord[1][1]
            );
          });
          setXCoords([param.data.coord[0][0], param.data.coord[1][0]]);
          setYCoords([param.data.coord[0][1], param.data.coord[1][1]]);
          setSegmentedCandidates(filteredData);
          setSegmentTitle(param.data.name);
          setIsModalOpen(true);
        }
      }
      if (param.componentType === 'series' && param.data) {
        // Handle email selection for candidates ONLY on Unsuccessful tab
        if (
          props.onApplicantSelection &&
          props.isApplicantSelected &&
          (props.currentTab === 'Unsuccessful Applicants' || svgActiveTab === 5)
        ) {
          // Check if applicant is currently selected to toggle the selection
          const isCurrentlySelected = props.isApplicantSelected(param.data);
          props.onApplicantSelection(param.data, !isCurrentlySelected);
          return;
        }

        const matchedCandidates = candidates.filter(
          (candidate) =>
            candidate.value[0] === param.value[0] &&
            candidate.value[1] === param.value[1],
        );

        if (matchedCandidates.length === 1) {
          // Fixed: Ensure single candidate click always works
          if (props.onCandidateClick) {
            // For single candidates, also try to detect tile and pass tile info
            const candidateCoords = [param.value[0], param.value[1]];
            let tileName = '';
            let tileCandidates: Applicant[] = [];

            // Check each segment in segmentedData to find which tile contains these coordinates
            for (const segment of segmentedData) {
              if (segment.length >= 2) {
                const [start, end] = segment;
                if (
                  start.xAxis !== undefined &&
                  end.xAxis !== undefined &&
                  start.yAxis !== undefined &&
                  end.yAxis !== undefined &&
                  candidateCoords[0] >= start.xAxis &&
                  candidateCoords[0] <= end.xAxis &&
                  candidateCoords[1] >= start.yAxis &&
                  candidateCoords[1] <= end.yAxis
                ) {
                  tileName = start.name || '';
                  tileCandidates = candidates.filter((item) => {
                    return (
                      start.xAxis !== undefined &&
                      end.xAxis !== undefined &&
                      start.yAxis !== undefined &&
                      end.yAxis !== undefined &&
                      item.value[0] >= start.xAxis &&
                      item.value[0] <= end.xAxis &&
                      item.value[1] >= start.yAxis &&
                      item.value[1] <= end.yAxis
                    );
                  });
                  break;
                }
              }
            }

            // Always trigger single candidate click
            props.onCandidateClick(
              matchedCandidates[0],
              tileCandidates.length > 0
                ? tileCandidates
                : [matchedCandidates[0]],
              tileName || 'Individual Candidate',
            );
            return;
          }

          // Fallback to old tab system
          const nameExists = selectedCandidates.some(
            (item) =>
              item.candidate && item.candidate.userId === param.data.userId,
          );

          if (!nameExists) {
            const updatedTabTitles = [...tabTitles, param.data.name];
            const selectedItem: Applicant | undefined = candidates.find(
              (item) => item.userId === param.data.userId,
            );
            const newSelectedCandidates = [
              ...selectedCandidates,
              {
                tabId: tabTitles.length,
                candidate: selectedItem,
              },
            ];
            setSelectedCandidates(newSelectedCandidates);
            setTabTitles(updatedTabTitles);
            setActiveTab(tabTitles.length);
          } else {
            toast.error('Tab for ' + param.data.name + ' already open', {
              position: 'bottom-center',
            });
          }
        } else {
          // Multiple candidates at the same exact coordinates - use same filtering as tooltip
          const candidateCoords = [param.value[0], param.value[1]];
          let tileName = '';

          // First, ensure we use the exact same filtering as the tooltip
          const exactMatchedCandidates = candidates.filter(
            (candidate) =>
              candidate.value[0] === param.value[0] &&
              candidate.value[1] === param.value[1],
          );

          // Check each segment in segmentedData to find which tile contains these coordinates for naming
          for (const segment of segmentedData) {
            if (segment.length >= 2) {
              const [start, end] = segment;
              if (
                start.xAxis !== undefined &&
                end.xAxis !== undefined &&
                start.yAxis !== undefined &&
                end.yAxis !== undefined &&
                candidateCoords[0] >= start.xAxis &&
                candidateCoords[0] <= end.xAxis &&
                candidateCoords[1] >= start.yAxis &&
                candidateCoords[1] <= end.yAxis
              ) {
                tileName = start.name || '';
                break;
              }
            }
          }

          // Set up for ScatterModal with exactly matched candidates (same as tooltip)
          setCurrentTileName(tileName || 'Multiple Candidates');
          setSeriesCandidates(exactMatchedCandidates);
          setIsSeriesModal(true);
        }
      }
    }
  };

  const analyticsPanel = renderTabComponent();

  const chartOption = useMemo(() => Get2DChartOptions({
    candidates,
    useSkillPercentage,
    skillValue,
    experienceValue,
    markLines,
    nineBox,
    segmentedData,
    selectedApplicantsForEmail: props.selectedApplicantsForEmail,
    isApplicantSelected: props.isApplicantSelected,
    currentTab: props.currentTab,
    showHeadshots,
    filteredSkills: additionalSkills?.skills.map((s) => s.skill),
    compact: isCompactChart,
  }), [candidates, useSkillPercentage, skillValue, experienceValue, markLines, nineBox, segmentedData, props.selectedApplicantsForEmail, props.isApplicantSelected, props.currentTab, showHeadshots, additionalSkills, isCompactChart]);

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Box
        sx={{
          display: 'flex',
          minWidth: 0,
          flexDirection: { xs: 'column', xl: 'row' },
          alignItems: { xl: 'stretch' },
          gap: 1.5,
        }}
      >
        {!view3D ? (
          loading ? (
            <Paper
              variant='outlined'
              sx={{
                height: { xs: 520, md: 620 },
                width: '100%',
                borderRadius: 2,
                p: 1,
              }}
            >
              <Skeleton variant='rectangular' width='100%' height='100%' sx={{ borderRadius: 2 }} />
            </Paper>
          ) : (
            <Paper
              variant='outlined'
              sx={{
                minWidth: 0,
                flex: 1,
                borderRadius: 2,
                p: { xs: 1, sm: 1.5 },
                bgcolor: 'background.paper',
              }}
            >
              <ReactECharts
                option={chartOption}
                style={{
                  height: chartHeight,
                  width: '100%',
                }}
                opts={{ renderer: 'svg' }}
                notMerge={false}
                lazyUpdate={true}
                onEvents={{ click: onChartClick }}
              />
            </Paper>
          )
        ) : (
          <Bar3D
            scatter={scatter}
            svgActiveTab={svgActiveTab}
            data={candidates}
            setSelectedCandidates={setSelectedCandidates}
            selectedCandidates={selectedCandidates}
            setActiveTab={setActiveTab}
            setTabTitles={setTabTitles}
            tabTitles={tabTitles}
            colorList={colorList}
            projection={projection}
            boxWidthDepth={boxWidthDepth}
            maxLegion={maxLegion}
            useSkillPercentage={useSkillPercentage}
            additionalSkills={additionalSkills}
            behavioralAttributes={behavioralAttributes}
            jobTitles={jobTitles}
            keywords={keywords}
            selectedApplicantsForEmail={
              props.selectedApplicantsForEmail
                ? new Set(
                  props.selectedApplicantsForEmail.map(
                    (applicant) => applicant.userId,
                  ),
                )
                : undefined
            }
            onApplicantSelection={
              props.onApplicantSelection
                ? (applicant: any) =>
                  props.onApplicantSelection!(applicant, true)
                : undefined
            }
            isApplicantSelected={props.isApplicantSelected}
            currentTab={props.currentTab}
            onCandidateClick={onCandidateClick}
          />
        )}
        {analyticsPanel && (
          <Paper
            variant='outlined'
            sx={{
              width: { xs: '100%', xl: 320 },
              flexShrink: 0,
              borderRadius: 2,
              p: 1.5,
              bgcolor: 'background.paper',
            }}
          >
            {analyticsPanel}
          </Paper>
        )}
      </Box>
      <Modal
        visible={isModalOpen}
        toggle={() => setIsModalOpen(false)}
        modalContent={loadModalContent()}
        maxWidth={false}
        fullWidth
        sx={{
          width: { xs: '95vw', sm: '92vw', lg: '90vw' },
          maxWidth: '1600px',
          height: { xs: '92vh', sm: '88vh' },
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          m: 'auto',
        }}
        dialogContentSx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
        customClass={styles.modalCustomClass}
        headerClass={styles.headerBackground}
        headerText={segmentTitle}
      />
      <Modal
        visible={isSeriesModal}
        toggle={() => setIsSeriesModal(false)}
        modalContent={loadSeriesModalContent()}
        maxWidth={false}
        fullWidth
        sx={{
          width: { xs: '95vw', sm: '92vw', lg: '90vw' },
          maxWidth: '1600px',
          height: { xs: '92vh', sm: '88vh' },
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          m: 'auto',
        }}
        dialogContentSx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
        customClass={styles.modalCustomClass}
        headerClass={styles.headerBackground}
        headerText={`Number of Candidates: ${seriesCandidates.length}`}
      />
    </Box>
  );
};
